import type { Draft } from './outbox';

/*
 * Что чат помнит на самом устройстве. Пока здесь очередь неотправленного
 * (решение 04.11.2026): закрыли вкладку или убили приложение посреди загрузки —
 * после запуска сообщение на месте с кнопкой «Повторить», а не пропало.
 *
 * Ключ — человек: на телефоне могут сменить пользователя, чужая очередь
 * не должна показаться следующему.
 *
 * Нет IndexedDB (приватное окно, старый браузер, тесты) или он ошибся —
 * хранилище молча работает как пустое: отправка из-за этого не ломается.
 */

export type QueueStore = {
  /** Очередь человека; снимает пометку «забыт», поставленную clear. */
  load(userId: string): Promise<Draft[]>;
  /** Записать один черновик. Пока человек забыт (clear), ничего не пишет. */
  put(userId: string, draft: Draft): Promise<void>;
  /** Убрать один черновик: доставлен или удалён человеком. */
  remove(userId: string, key: string): Promise<void>;
  /**
   * Стереть всю очередь человека и пометить его забытым: запись из ещё живой
   * вкладки (например, сорвавшаяся загрузка уходящего человека) не должна
   * вернуть стёртое. Пометка живёт до следующего load этого человека.
   */
  clear(userId: string): Promise<void>;
};

const DB_NAME = 'code-chat';
const STORE = 'outbox';

/*
 * Один черновик — одна запись с ключом [человек, ключ черновика]. Целая очередь
 * одной записью затирала бы чужую вкладку, а ошибка места из-за одного
 * большого видео убивала бы запись всех остальных.
 */
export function indexedDbQueueStore(): QueueStore {
  let opened: Promise<IDBDatabase | null> | null = null;
  const forgotten = new Set<string>();
  const open = (): Promise<IDBDatabase | null> => {
    if (opened) return opened;
    const mine: Promise<IDBDatabase | null> = new Promise<IDBDatabase | null>((resolve) => {
      const dead = () => {
        // не держим мёртвое соединение: следующая операция откроет новое
        if (opened === mine) opened = null;
      };
      try {
        if (typeof indexedDB === 'undefined') return resolve(null);
        const req = indexedDB.open(DB_NAME, 2);
        let gaveUp = false;
        req.onupgradeneeded = () => {
          const db = req.result;
          // версия 1 писала всю очередь одной записью — формат другой, старое сбрасываем
          if (db.objectStoreNames.contains(STORE)) db.deleteObjectStore(STORE);
          db.createObjectStore(STORE);
        };
        req.onsuccess = () => {
          const db = req.result;
          // iOS обрывает соединение, когда приложение уходит в фон
          db.onclose = dead;
          db.onversionchange = () => {
            db.close();
            dead();
          };
          // успели сдаться по onblocked — запоздавшее соединение не копим
          if (gaveUp) db.close();
          else resolve(db);
        };
        req.onerror = () => {
          dead();
          resolve(null);
        };
        req.onblocked = () => {
          gaveUp = true;
          dead();
          resolve(null);
        };
      } catch {
        dead();
        resolve(null);
      }
    });
    opened = mine;
    return mine;
  };
  /* Одна операция над хранилищем; любой сбой — fallback, а не исключение. */
  const run = async <T>(
    mode: IDBTransactionMode,
    fallback: T,
    op: (s: IDBObjectStore) => IDBRequest,
    retry = true,
  ): Promise<T> => {
    const conn = open();
    const db = await conn;
    if (!db) return fallback;
    try {
      return await new Promise<T>((resolve) => {
        const tx = db.transaction(STORE, mode);
        const req = op(tx.objectStore(STORE));
        tx.oncomplete = () => resolve((req.result as T | undefined) ?? fallback);
        tx.onerror = () => resolve(fallback);
        tx.onabort = () => resolve(fallback);
      });
    } catch {
      // соединение умерло (iOS): открываем заново и пробуем один раз
      try {
        db.close();
      } catch {
        // уже закрыто
      }
      if (opened === conn) opened = null;
      return retry ? run(mode, fallback, op, false) : fallback;
    }
  };
  const range = (userId: string) => IDBKeyRange.bound([userId], [userId, []]);
  return {
    load: (userId) => {
      forgotten.delete(userId);
      return run<Draft[]>('readonly', [], (s) => s.getAll(range(userId)));
    },
    put: (userId, draft) =>
      forgotten.has(userId)
        ? Promise.resolve()
        : run<void>('readwrite', undefined, (s) => s.put(draft, [userId, draft.key])).then(() => undefined),
    remove: (userId, key) =>
      forgotten.has(userId)
        ? Promise.resolve()
        : run<void>('readwrite', undefined, (s) => s.delete([userId, key])).then(() => undefined),
    clear: (userId) => {
      forgotten.add(userId);
      return run<void>('readwrite', undefined, (s) => s.delete(range(userId))).then(() => undefined);
    },
  };
}

/** Для тестов: тот же договор, только в памяти. Общая карта — «общая база» нескольких вкладок. */
export function memoryQueueStore(): QueueStore {
  const data = new Map<string, Map<string, Draft>>();
  const forgotten = new Set<string>();
  const of = (userId: string) => {
    let m = data.get(userId);
    if (!m) data.set(userId, (m = new Map()));
    return m;
  };
  return {
    load: async (userId) => {
      forgotten.delete(userId);
      return [...of(userId).values()];
    },
    put: async (userId, draft) => {
      if (!forgotten.has(userId)) of(userId).set(draft.key, draft);
    },
    remove: async (userId, key) => {
      if (!forgotten.has(userId)) of(userId).delete(key);
    },
    clear: async (userId) => {
      forgotten.add(userId);
      data.delete(userId);
    },
  };
}
